import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from "@angular/core";
import { CurrencyPipe, DatePipe, NgClass } from "@angular/common";
import { RouterModule } from "@angular/router";
import { ActivatedRoute, Router } from "@angular/router";
import { FormsModule } from "@angular/forms";
import { rxResource, toSignal } from "@angular/core/rxjs-interop";
import { catchError, firstValueFrom, forkJoin, map, of, switchMap } from "rxjs";
import { ApiService } from "../core/api.service";
import { ConfirmDialogService } from "../core/confirm-dialog.service";
import {
  stageLabel,
  supplierStage,
  supplierSteps,
} from "../orders/supplier.util";
import {
  CatalogBlind,
  Customer,
  EstimateVersion,
  Measurement,
  MeasurementInput,
  PaymentRecord,
  Room,
  WindowEntity,
  WindowInput,
} from "../core/models";

@Component({
  selector: "app-customer-detail",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterModule, FormsModule, CurrencyPipe, DatePipe, NgClass],
  templateUrl: "./customer-detail.component.html",
  styleUrls: ["./customer-detail.component.scss"],
})
export class CustomerDetailComponent {
  private readonly api = inject(ApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly confirmDialog = inject(ConfirmDialogService);

  readonly customerId = toSignal(
    this.route.paramMap.pipe(map((params) => params.get("customerId") ?? "")),
    { initialValue: this.route.snapshot.paramMap.get("customerId") ?? "" },
  );
  roomForm = { name: "", windows: 1 };
  readonly activeTab = signal<"overview" | "measurements" | "estimate">(
    "overview",
  );
  readonly creatingRoom = signal(false);

  readonly message = signal("");
  readonly savingCustomer = signal(false);
  readonly deletingCustomer = signal(false);
  readonly archivingCustomer = signal(false);
  readonly approvingOrder = signal(false);
  readonly approveError = signal("");

  paymentForm = { amount: 0, date: "", method: "", note: "" };
  private readonly estimateResource = rxResource({
    params: () => this.customerId(),
    // A job may have no estimate yet (404) — treat it as "no estimate" rather
    // than letting the error propagate and abort change detection.
    stream: ({ params }) =>
      this.api.getEstimate(params).pipe(catchError(() => of(null))),
  });
  readonly estimateTotal = computed(
    () => this.estimateResource.value()?.total ?? null,
  );
  readonly savingPayment = signal(false);
  readonly requestingLink = signal(false);
  readonly paymentLink = signal<string | null>(null);
  readonly paymentLinkError = signal("");
  linkAmount: number | null = null;

  private readonly estimateVersionsResource = rxResource({
    params: () => this.customerId(),
    stream: ({ params }) =>
      this.api
        .getEstimateVersions(params)
        .pipe(catchError(() => of([] as EstimateVersion[]))),
  });
  readonly estimateVersions = this.estimateVersionsResource.value;
  readonly estimateVersionCount = computed(
    () => (this.estimateVersions() ?? []).length,
  );

  readonly remakeOpen = signal(false);
  readonly remakeLoading = signal(false);
  readonly remakeSaving = signal(false);
  readonly remakeMessage = signal("");
  readonly remakeWindows = signal<RemakeWindowRow[]>([]);

  private readonly customerResource = rxResource({
    params: () => this.customerId(),
    stream: ({ params }) =>
      this.api.getCustomer(params).pipe(
        catchError(() => {
          this.message.set("Failed to load customer.");
          return of(null as Customer | null);
        }),
      ),
  });
  readonly customer = this.customerResource.value;

  private readonly roomsResource = rxResource({
    params: () => this.customerId(),
    stream: ({ params }) =>
      this.api.getRooms(params).pipe(
        catchError(() => {
          this.message.set("Failed to load rooms.");
          return of([] as Room[]);
        }),
      ),
  });
  readonly rooms = this.roomsResource.value;

  private readonly windowsResource = rxResource({
    params: () => this.customerId(),
    stream: ({ params }) =>
      // One aggregate request returns windows for all rooms.
      this.api.getCustomerWindows(params).pipe(
        catchError(() => {
          this.message.set("Failed to load windows.");
          return of([] as WindowEntity[]);
        }),
      ),
  });
  readonly windows = this.windowsResource.value;

  readonly roomWindowCounts = computed<Record<string, number>>(() => {
    const counts: Record<string, number> = {};
    for (const window of this.windows() ?? []) {
      counts[window.roomId] = (counts[window.roomId] ?? 0) + 1;
    }
    return counts;
  });

  readonly windowsByRoom = computed<Record<string, WindowEntity[]>>(() => {
    const byRoom: Record<string, WindowEntity[]> = {};
    for (const window of this.windows() ?? []) {
      (byRoom[window.roomId] ??= []).push(window);
    }
    return byRoom;
  });

  readonly windowsCount = computed(() => (this.windows() ?? []).length);

  private readonly measurementsResource = rxResource({
    params: () => this.windows(),
    stream: ({ params: windows }) => {
      if (!windows.length) return of([] as Measurement[]);
      return this.api
        .getMeasurementsBulk(windows.map((w) => w.id))
        .pipe(catchError(() => of([] as Measurement[])));
    },
  });
  readonly measurements = this.measurementsResource.value;

  private readonly suppliersResource = rxResource({
    stream: () =>
      this.api
        .listSuppliers()
        .pipe(
          catchError(() =>
            of(
              [] as {
                id: string;
                name: string;
                email: string;
                createdAt: string;
              }[],
            ),
          ),
        ),
  });
  readonly suppliers = this.suppliersResource.value;

  /** Latest measurement per window (by createdAt). */
  readonly measurementsByWindow = computed<Record<string, Measurement>>(() => {
    const latest: Record<string, Measurement> = {};
    for (const measurement of this.measurements() ?? []) {
      const current = latest[measurement.windowId];
      if (
        !current ||
        (measurement.createdAt ?? "") > (current.createdAt ?? "")
      ) {
        latest[measurement.windowId] = measurement;
      }
    }
    return latest;
  });

  /** Blind codes from the catalog, falling back to styles already in use. */
  private readonly styleOptionsResource = rxResource({
    params: () => this.customerId(),
    stream: ({ params }) =>
      this.api.getCatalogBlinds().pipe(
        catchError(() => of([] as CatalogBlind[])),
        switchMap((blinds) =>
          blinds.length
            ? of(blinds)
            : this.api.getCustomerWindows(params).pipe(
                map((allWindows) => {
                  const styles = allWindows
                    .map((w) => w.style)
                    .filter((s): s is string => !!s && s.trim().length > 0);
                  return [...new Set(styles)].map(
                    (code) =>
                      ({
                        id: code,
                        name: code,
                        semiBlackoutCode: code,
                        blackoutCode: code,
                        createdAt: "",
                      }) as CatalogBlind,
                  );
                }),
                catchError(() => of([] as CatalogBlind[])),
              ),
        ),
      ),
  });

  readonly styleChoices = computed(() => {
    const seen = new Set<string>();
    const choices: { value: string; label: string }[] = [];
    for (const blind of this.styleOptionsResource.value() ?? []) {
      const pairs: [string, string][] = [
        [blind.semiBlackoutCode, "Semi blackout"],
        [blind.blackoutCode, "Blackout"],
      ];
      for (const [code, suffix] of pairs) {
        if (code && !seen.has(code)) {
          seen.add(code);
          choices.push({ value: code, label: `${blind.name} — ${suffix}` });
        }
      }
    }
    for (const window of this.windows() ?? []) {
      const style = window.style?.trim();
      if (style && !seen.has(style)) {
        seen.add(style);
        choices.push({ value: style, label: style });
      }
    }
    return choices;
  });

  private readonly allCustomersResource = rxResource({
    params: () => this.customerId(),
    stream: () =>
      this.api
        .getCustomers()
        .pipe(catchError(() => of([] as Customer[]))),
  });

  /** The original job when this customer is itself a remake. */
  readonly originalCustomer = computed(() => {
    const remakeOf = this.customer()?.remakeOfCustomerId;
    if (!remakeOf) return null;
    return (
      (this.allCustomersResource.value() ?? []).find((c) => c.id === remakeOf) ??
      null
    );
  });

  /** Jobs that are remakes of this customer. */
  readonly remakes = computed(() =>
    (this.allCustomersResource.value() ?? [])
      .filter((c) => c.remakeOfCustomerId === this.customerId())
      .sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? "")),
  );

  readonly payments = computed(() => this.customer()?.payments ?? []);
  readonly totalPaid = computed(() =>
    this.payments().reduce((s, p) => s + (Number(p.amount) || 0), 0),
  );
  readonly balance = computed(() => {
    const total = this.estimateTotal();
    return total === null ? null : total - this.totalPaid();
  });

  editCustomer() {
    if (!this.customer()) {
      return;
    }
    this.router.navigate(["/customers", this.customerId(), "edit"]);
  }

  async deleteCustomer() {
    if (this.deletingCustomer()) return;
    const confirmed = await this.confirmDialog.confirm({
      title: "Delete customer",
      message: "Delete this customer and all rooms/windows/measurements?",
      confirmText: "Delete",
      destructive: true,
    });
    if (!confirmed) {
      return;
    }

    this.deletingCustomer.set(true);
    this.api.deleteCustomer(this.customerId()).subscribe({
      next: () => {
        this.router.navigate(["/jobs"]);
      },
      error: () => {
        this.message.set("Failed to delete customer.");
        this.deletingCustomer.set(false);
      },
    });
  }

  async archiveCustomer() {
    if (this.archivingCustomer()) return;
    const confirmed = await this.confirmDialog.confirm({
      title: "Archive job",
      message: "Archive this job? You can restore it later from the Jobs list.",
      confirmText: "Archive",
      destructive: true,
    });
    if (!confirmed) {
      return;
    }

    this.archivingCustomer.set(true);
    this.api.archiveCustomer(this.customerId()).subscribe({
      next: () => {
        this.router.navigate(["/jobs"]);
      },
      error: (err) => {
        this.archivingCustomer.set(false);
        this.message.set(
          err?.status === 404
            ? "Archive isn't available yet."
            : "Failed to archive job.",
        );
      },
    });
  }

  restoreCustomer() {
    if (this.archivingCustomer()) return;
    this.archivingCustomer.set(true);
    this.api.restoreCustomer(this.customerId()).subscribe({
      next: (updated) => {
        this.customerResource.set(updated);
        this.archivingCustomer.set(false);
        this.message.set("Job restored.");
      },
      error: (err) => {
        this.archivingCustomer.set(false);
        this.message.set(
          err?.status === 404
            ? "Restore isn't available yet."
            : "Failed to restore job.",
        );
      },
    });
  }

  setTab(tab: "overview" | "measurements" | "estimate") {
    this.activeTab.set(tab);
  }

  windowsForRoom(roomId: string): WindowEntity[] {
    return this.windowsByRoom()[roomId] ?? [];
  }

  measurementFor(windowId: string): Measurement | null {
    return this.measurementsByWindow()[windowId] ?? null;
  }

  createRoom() {
    const name = this.roomForm.name.trim();
    if (!name) {
      this.message.set("Room name is required.");
      return;
    }
    const count = Math.min(
      20,
      Math.max(1, Math.round(Number(this.roomForm.windows) || 1)),
    );
    this.creatingRoom.set(true);
    this.api
      .createRoom(this.customerId(), { name })
      .pipe(
        switchMap((room) =>
          forkJoin(
            Array.from({ length: count }, (_, i) =>
              this.api.createWindow(room.id, { label: `Window ${i + 1}` }),
            ),
          ).pipe(map((created) => ({ room, created }))),
        ),
      )
      .subscribe({
        next: ({ room, created }) => {
          this.roomsResource.update((rooms) =>
            [...(rooms ?? []), room].sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
            ),
          );
          this.windowsResource.update((windows) => [
            ...(windows ?? []),
            ...created,
          ]);
          this.roomForm = { name: "", windows: 1 };
          this.creatingRoom.set(false);
          this.message.set(
            `Room added with ${created.length} window${
              created.length === 1 ? "" : "s"
            }.`,
          );
        },
        error: () => {
          this.creatingRoom.set(false);
          this.message.set("Failed to create room.");
        },
      });
  }

  /** Adds one window to an existing room (the room page was removed). */
  addWindow(room: Room) {
    const label = `Window ${this.windowsForRoom(room.id).length + 1}`;
    this.api.createWindow(room.id, { label }).subscribe({
      next: (window) =>
        this.windowsResource.update((windows) => [...(windows ?? []), window]),
      error: () => this.message.set("Failed to add window."),
    });
  }

  /** Own-Job pipeline, in order; used by the Overview progress stepper. */
  readonly jobSteps = [
    { status: "Onboarded", label: "Onboarded" },
    { status: "Measured", label: "Measured" },
    { status: "Estimate given", label: "Estimate given" },
    { status: "Advance paid", label: "Advance paid" },
    { status: "In production", label: "In production" },
    { status: "Delivered", label: "Delivered" },
    { status: "Installed", label: "Installed" },
    { status: "Complete paid", label: "Complete paid" },
  ];

  jobStepIndex(customer: { status?: string }): number {
    const i = this.jobSteps.findIndex((s) => s.status === customer.status);
    const fromStatus = i < 0 ? 0 : i;
    // Measurements on file advance the bar even if the manual status lags
    // (adding measurements doesn't set the status today).
    const hasMeasurements = (this.measurements() ?? []).length > 0;
    return Math.max(fromStatus, hasMeasurements ? 1 : 0);
  }

  readonly supplierSaving = signal(false);
  readonly stageLabel = stageLabel;

  readonly trackingUrl = computed(() => {
    const tracking = this.customer()?.trackingNumber?.trim();
    return tracking
      ? `https://www.aftership.com/track?t=${encodeURIComponent(tracking)}&c=sf-express`
      : null;
  });

  /** Per-supplier cards for the job — same model as the DIY supplier card. */
  readonly jobSupplierCards = computed(() => {
    const c = this.customer();
    if (!c) return [];
    const ids = c.supplierIds?.length
      ? c.supplierIds
      : c.supplierId
        ? [c.supplierId]
        : [];
    const list = this.suppliers() ?? [];
    return ids.map((id) => {
      const supplier = list.find((s) => s.id === id);
      return {
        id,
        name: supplier?.name ?? "Unknown supplier",
        email: supplier?.email ?? "",
        status:
          c.supplierQuoteStatuses?.[id] ??
          (c.supplierId === id ? c.supplierQuoteStatus : undefined) ??
          "pending",
        quote:
          c.supplierQuotes?.[id] ??
          (c.supplierId === id ? c.supplierQuote : undefined),
        alibabaUrl: c.supplierAlibabaUrls?.[id] ?? null,
        hasTracking: !!c.trackingNumber,
        scope: this.jobSupplierScope(id),
      };
    });
  });

  stageFor(status: string) {
    return supplierStage(status);
  }

  stepsFor(status: string, hasAlibaba: boolean, hasTracking: boolean) {
    return supplierSteps(status, hasAlibaba, hasTracking);
  }

  /** "Living Room · Kitchen" for the windows routed to this supplier. */
  jobSupplierScope(supplierId: string): string {
    const windows = (this.windows() ?? []).filter(
      (w) => w.supplierId === supplierId,
    );
    if (!windows.length) return "";
    const roomNames = new Set(windows.map((w) => w.roomId));
    const names = (this.rooms() ?? [])
      .filter((r) => roomNames.has(r.id))
      .map((r) => r.name);
    return names.join(" · ");
  }

  markJobPaid(supplierId: string) {
    this.supplierSaving.set(true);
    this.api.markJobSupplierPaid(this.customerId(), supplierId).subscribe({
      next: () => {
        this.supplierSaving.set(false);
        this.customerResource.reload();
        this.message.set("Marked paid — supplier can start production.");
      },
      error: (err) => {
        this.supplierSaving.set(false);
        this.message.set(err?.error?.message ?? "Failed to mark paid.");
      },
    });
  }

  approveJobQuote(supplierId: string) {
    this.supplierSaving.set(true);
    this.api.approveSupplierQuote(this.customerId(), supplierId).subscribe({
      next: () => {
        this.supplierSaving.set(false);
        this.customerResource.reload();
        this.message.set("Quote approved.");
      },
      error: (err) => {
        this.supplierSaving.set(false);
        this.message.set(err?.error?.message ?? "Failed to approve quote.");
      },
    });
  }

  rejectJobQuote(supplierId: string) {
    this.supplierSaving.set(true);
    this.api.rejectSupplierQuote(this.customerId(), supplierId).subscribe({
      next: () => {
        this.supplierSaving.set(false);
        this.customerResource.reload();
        this.message.set("Quote rejected.");
      },
      error: (err) => {
        this.supplierSaving.set(false);
        this.message.set(err?.error?.message ?? "Failed to reject quote.");
      },
    });
  }

  /** Routes each window to its blind's supplier and notifies the suppliers. */
  approveOrder() {
    this.approveError.set("");
    this.approvingOrder.set(true);
    this.api.approveOrder(this.customerId()).subscribe({
      next: () => {
        this.approvingOrder.set(false);
        this.customerResource.reload();
        this.message.set("Sent to supplier — they've been emailed a portal link.");
      },
      error: (err) => {
        this.approvingOrder.set(false);
        const unsupplied = err?.error?.unsupplied as
          | { roomName: string; windowLabel: string }[]
          | undefined;
        this.approveError.set(
          unsupplied?.length
            ? `No supplier for: ${unsupplied
                .map((u) => `${u.roomName} / ${u.windowLabel}`)
                .join(", ")}. Set it in the catalog first.`
            : (err?.error?.message ?? "Failed to send to supplier."),
        );
      },
    });
  }

  async deleteRoom(room: Room) {    const confirmed = await this.confirmDialog.confirm({
      title: "Delete room",
      message: `Delete "${room.name}" and all its windows/measurements?`,
      confirmText: "Delete",
      destructive: true,
    });
    if (!confirmed) {
      return;
    }
    this.api.deleteRoom(this.customerId(), room.id).subscribe({
      next: () => {
        this.roomsResource.update((rooms) =>
          (rooms ?? []).filter((r) => r.id !== room.id),
        );
        this.windowsResource.update((windows) =>
          (windows ?? []).filter((w) => w.roomId !== room.id),
        );
        this.message.set("Room deleted.");
      },
      error: () => this.message.set("Failed to delete room."),
    });
  }

  async deleteWindow(room: Room, window: WindowEntity) {
    const confirmed = await this.confirmDialog.confirm({
      title: "Delete window",
      message: `Delete "${window.label}" and its measurements?`,
      confirmText: "Delete",
      destructive: true,
    });
    if (!confirmed) return;
    this.api.deleteWindow(room.id, window.id).subscribe({
      next: () => {
        this.windowsResource.update((windows) =>
          (windows ?? []).filter((w) => w.id !== window.id),
        );
        this.message.set("Window deleted.");
      },
      error: () => this.message.set("Failed to delete window."),
    });
  }

  updateWindowLabel(room: Room, window: WindowEntity, label: string) {
    const value = label.trim();
    if (!value || value === window.label) return;
    this.api.updateWindow(room.id, window.id, { label: value }).subscribe({
      next: (updated) =>
        this.windowsResource.update((windows) =>
          (windows ?? []).map((w) => (w.id === updated.id ? updated : w)),
        ),
      error: () => this.message.set("Failed to update window label."),
    });
  }

  updateWindowStyle(room: Room, window: WindowEntity, style: string) {
    this.api
      .updateWindow(room.id, window.id, { style: style || undefined })
      .subscribe({
        next: (updated) =>
          this.windowsResource.update((windows) =>
            (windows ?? []).map((w) => (w.id === updated.id ? updated : w)),
          ),
        error: () => this.message.set("Failed to update style."),
      });
  }

  updateWindowControl(room: Room, window: WindowEntity, controlType: string) {
    const payload: Partial<WindowInput> = {
      controlType: controlType as WindowInput["controlType"],
      hasSolarPanel:
        controlType === "motorized" ? !!window.hasSolarPanel : false,
    };
    this.api.updateWindow(room.id, window.id, payload).subscribe({
      next: (updated) =>
        this.windowsResource.update((windows) =>
          (windows ?? []).map((w) => (w.id === updated.id ? updated : w)),
        ),
      error: () => this.message.set("Failed to update control."),
    });
  }

  updateWindowSolar(room: Room, window: WindowEntity, checked: boolean) {
    this.api
      .updateWindow(room.id, window.id, { hasSolarPanel: checked })
      .subscribe({
        next: (updated) =>
          this.windowsResource.update((windows) =>
            (windows ?? []).map((w) => (w.id === updated.id ? updated : w)),
          ),
        error: () => this.message.set("Failed to update solar panel."),
      });
  }

  setWidth(window: WindowEntity, value: number | string) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) this.persistDimension(window, "width", n);
  }

  setHeight(window: WindowEntity, value: number | string) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) this.persistDimension(window, "height", n);
  }

  private persistDimension(
    window: WindowEntity,
    dim: "width" | "height",
    value: number,
  ) {
    const existing = this.measurementFor(window.id);
    if (existing) {
      const payload: Partial<MeasurementInput> = {
        width: dim === "width" ? value : existing.width,
        height: dim === "height" ? value : existing.height,
        unit: existing.unit,
      };
      this.api.updateMeasurement(window.id, existing.id, payload).subscribe({
        next: (saved) => {
          this.measurementsResource.update((list) =>
            (list ?? []).map((m) => (m.id === saved.id ? saved : m)),
          );
          this.advanceToMeasured();
        },
        error: () => this.message.set("Failed to save measurement."),
      });
      return;
    }
    // First edit on a window with no measurement: create one, defaulting the
    // untouched dimension to the storefront defaults (30" x 60").
    const payload: MeasurementInput = {
      width: dim === "width" ? value : 30,
      height: dim === "height" ? value : 60,
      unit: "in",
    };
    this.api.createMeasurement(window.id, payload).subscribe({
      next: (created) => {
        this.measurementsResource.update((list) => [...(list ?? []), created]);
        this.advanceToMeasured();
      },
      error: () => this.message.set("Failed to save measurement."),
    });
  }

  /** Adding measurements advances an untouched job to "Measured". */
  private advanceToMeasured() {
    const c = this.customer();
    if (!c || (c.status && c.status !== "Onboarded")) return;
    this.api
      .updateCustomer(this.customerId(), { status: "Measured" })
      .subscribe({ next: () => this.customerResource.reload() });
  }

  openRemake() {
    const roomNames = new Map(
      (this.rooms() ?? []).map((room) => [room.id, room.name]),
    );
    this.remakeLoading.set(true);
    this.remakeMessage.set("");
    this.remakeWindows.set([]);
    this.remakeOpen.set(true);
    this.api.getCustomerWindows(this.customerId()).subscribe({
      next: (windows) => {
        if (!this.remakeOpen()) return;
        if (windows.length === 0) {
          this.remakeLoading.set(false);
          this.remakeMessage.set("This job has no windows to remake.");
          return;
        }
        this.api.getMeasurementsBulk(windows.map((w) => w.id)).subscribe({
          next: (measurements) => {
            if (!this.remakeOpen()) return;
            const latest = new Map<string, Measurement>();
            for (const m of measurements) {
              const current = latest.get(m.windowId);
              if (!current || m.createdAt > current.createdAt) {
                latest.set(m.windowId, m);
              }
            }
            const rows: RemakeWindowRow[] = windows
              .filter((w) => latest.has(w.id))
              .map((w) => {
                const m = latest.get(w.id)!;
                return {
                  windowId: w.id,
                  roomName: roomNames.get(w.roomId) ?? w.roomId,
                  label: w.label,
                  unit: m.unit,
                  width: m.width,
                  height: m.height,
                  selected: false,
                };
              })
              .sort(
                (a, b) =>
                  a.roomName.localeCompare(b.roomName, undefined, {
                    sensitivity: "base",
                  }) ||
                  a.label.localeCompare(b.label, undefined, {
                    sensitivity: "base",
                  }),
              );
            this.remakeWindows.set(rows);
            this.remakeLoading.set(false);
          },
          error: () => {
            if (!this.remakeOpen()) return;
            this.remakeLoading.set(false);
            this.remakeMessage.set("Failed to load measurements.");
          },
        });
      },
      error: () => {
        if (!this.remakeOpen()) return;
        this.remakeLoading.set(false);
        this.remakeMessage.set("Failed to load windows.");
      },
    });
  }

  closeRemake() {
    this.remakeOpen.set(false);
    this.remakeWindows.set([]);
    this.remakeMessage.set("");
    this.remakeLoading.set(false);
    this.remakeSaving.set(false);
  }

  toggleRemakeWindow(windowId: string) {
    this.remakeWindows.update((rows) =>
      rows.map((r) =>
        r.windowId === windowId ? { ...r, selected: !r.selected } : r,
      ),
    );
  }

  submitRemake() {
    const selected = this.remakeWindows().filter((r) => r.selected);
    if (selected.length === 0) {
      this.remakeMessage.set("Select at least one window to remake.");
      return;
    }
    if (selected.some((r) => !(Number(r.width) > 0) || !(Number(r.height) > 0))) {
      this.remakeMessage.set("Corrected width and height are required.");
      return;
    }
    this.remakeSaving.set(true);
    this.api
      .createRemake(
        this.customerId(),
        selected.map((r) => ({
          windowId: r.windowId,
          width: Number(r.width),
          height: Number(r.height),
        })),
      )
      .subscribe({
        next: (created) => {
          this.remakeSaving.set(false);
          this.closeRemake();
          this.router.navigate(["/customers", created.id]);
        },
        error: () => {
          this.remakeSaving.set(false);
          this.remakeMessage.set("Failed to create remake.");
        },
      });
  }

  viewEstimate() {
    this.router.navigate(["/customers", this.customerId(), "estimate"]);
  }

  updateCustomerStatus() {
    const current = this.customer();
    if (!current) return;

    this.savingCustomer.set(true);
    const update = { status: current.status || undefined };

    this.api.updateCustomer(this.customerId(), update).subscribe({
      next: (updated) => {
        this.customerResource.set(updated);
        this.message.set("Status updated.");
        this.savingCustomer.set(false);
      },
      error: () => {
        this.message.set("Failed to update status.");
        this.savingCustomer.set(false);
      },
    });
  }

  saveTracking() {
    const current = this.customer();
    if (!current) return;

    this.savingCustomer.set(true);
    const value = current.trackingNumber?.trim() ?? "";

    this.api
      .updateCustomer(this.customerId(), { trackingNumber: value })
      .subscribe({
        next: (updated) => {
          this.customerResource.set(updated);
          this.message.set("Tracking number saved.");
          this.savingCustomer.set(false);
        },
        error: () => {
          this.message.set("Failed to save tracking number.");
          this.savingCustomer.set(false);
        },
      });
  }

  canEditTracking(customer: Customer | null): boolean {
    return (
      !!customer &&
      (customer.status === "Delivered" ||
        customer.status === "Installed" ||
        customer.status === "Complete paid" ||
        !!customer.trackingNumber?.trim())
    );
  }

  trackUrl(customer: Customer): string | null {
    const no = customer.trackingNumber?.trim();
    return no
      ? `https://www.aftership.com/track?t=${encodeURIComponent(no)}&c=sf-express`
      : null;
  }

  addPayment() {
    const amount = Number(this.paymentForm.amount) || 0;
    if (amount <= 0) {
      this.message.set("Payment amount must be greater than zero.");
      return;
    }
    const current = this.customer();
    if (!current) return;

    const payment: PaymentRecord = {
      id: `PAY-${crypto.randomUUID().slice(0, 8).toUpperCase()}`,
      amount,
      date: this.paymentForm.date || this.todayISO(),
      method: this.paymentForm.method.trim() || undefined,
      note: this.paymentForm.note.trim() || undefined,
    };

    this.savingPayment.set(true);
    this.api
      .updateCustomer(this.customerId(), {
        payments: [...this.payments(), payment],
      })
      .subscribe({
        next: (updated) => {
          this.customerResource.set(updated);
          this.paymentForm = { amount: 0, date: "", method: "", note: "" };
          this.message.set("Payment added.");
          this.savingPayment.set(false);
        },
        error: () => {
          this.message.set("Failed to add payment.");
          this.savingPayment.set(false);
        },
      });
  }

  async removePayment(paymentId: string) {
    const current = this.customer();
    if (!current) return;
    const confirmed = await this.confirmDialog.confirm({
      title: "Remove payment",
      message: "Remove this payment from the customer's records?",
      confirmText: "Remove",
      destructive: true,
    });
    if (!confirmed) return;
    this.savingPayment.set(true);
    this.api
      .updateCustomer(this.customerId(), {
        payments: this.payments().filter((p) => p.id !== paymentId),
      })
      .subscribe({
        next: (updated) => {
          this.customerResource.set(updated);
          this.message.set("Payment removed.");
          this.savingPayment.set(false);
        },
        error: () => {
          this.message.set("Failed to remove payment.");
          this.savingPayment.set(false);
        },
      });
  }

  async requestStripePayment() {
    const current = this.customer();
    if (!current) return;
    const amount = Number(
      this.linkAmount ?? this.balance() ?? this.estimateTotal() ?? 0,
    );
    if (!Number.isFinite(amount) || amount <= 0) {
      this.paymentLinkError.set("Enter an amount greater than zero.");
      return;
    }
    this.requestingLink.set(true);
    this.paymentLinkError.set("");
    this.paymentLink.set(null);
    try {
      const { url } = await firstValueFrom(
        this.api.createJobPaymentLink(current.id, amount),
      );
      this.paymentLink.set(url);
    } catch {
      this.paymentLinkError.set("Could not create the payment link.");
    } finally {
      this.requestingLink.set(false);
    }
  }

  copyPaymentLink() {
    const url = this.paymentLink();
    if (url) navigator.clipboard?.writeText(url);
  }

  private todayISO(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }
}

interface RemakeWindowRow {
  windowId: string;
  roomName: string;
  label: string;
  unit: "in" | "cm";
  width: number;
  height: number;
  selected: boolean;
}
